import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import get from 'lodash.get';
import { Flex, Typography, Box, SelectDropdown, Skeleton, Selector } from '@kistech/kis-ui';
import { Grid, Typography as MuiTypography, Box as MuiBox } from '@mui/material';
import { useActions } from 'hooks/useActions';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { actions as ciActions, selectors as ciSelectors, ClientIntegration } from 'store/clientIntegrations';
import { actions as configActions, selectors as configSelectors, Configuration } from 'store/configurations';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { getSortedList } from 'utils/stringFormat';
import OtherImages from './components/OtherImages';
import UpperDeckMediaDetails from './components/UpperDeckMediaDetails';
import { AttractLoopMediaDetails } from './components/AttractLoopMediaDetails';
import { EventImages } from './components/EventImages';
import { GenreImages } from './components/GenreImages';
import { Selections } from './types';
import { VenueImages } from './components/VenueImages';
import { useStyles } from './styled';

const MediaCenterView: React.FC = () => {
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const configurations = useSelector(configSelectors.selectConfigurations);
  const loading = useSelector(configSelectors.selectPending);
  const clientIntegrations = useSelector(ciSelectors.selectClientIntegrations);
  const selectedConfiguration = useSelector(mediaSelectors.selectConfiguration);
  const selectedCI = useSelector(mediaSelectors.selectEnvironment);

  const [
    getConfigurations,
    getClientIntegrations,
    setEnvironment,
    setGenre,
    setEvent,
    setVenue,
    setConfiguration,
    resetAction,
    resetMedia
  ] = useActions([
    configActions.getConfigurations,
    ciActions.getClientIntegrations,
    mediaActions.setEnvironment,
    mediaActions.setGenre,
    mediaActions.setEvent,
    mediaActions.setVenue,
    mediaActions.setConfiguration,
    mediaActions.resetAction,
    mediaActions.resetMedia
  ]);

  const [tabSelected, setTabSelected] = useState<Selections>(Selections.LOWER_DECK);
  const showConfigSelector = [Selections.UPPER_DECK, Selections.LOWER_DECK, Selections.OTHER_IMAGES].includes(
    tabSelected
  );
  const showEnvironmentSelector = [Selections.EVENT_IMAGES, Selections.VENUE_IMAGES, Selections.GENRE_IMAGES].includes(
    tabSelected
  );

  const upperMonitor = get(client, 'configuration.upperMonitor', false);
  const genreTab = get(client, 'configuration.genreTab', false);
  const classes = useStyles({ genreTab, upperMonitor })();

  React.useEffect(() => {
    getConfigurations();
    getClientIntegrations();
    setTabSelected(Selections.LOWER_DECK);

    return () => {
      resetMedia();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  React.useEffect(() => {
    if (!selectedConfiguration && configurations.length) {
      setConfiguration(configurations[0]);
    }
    if (selectedConfiguration && configurations.length) {
      setConfiguration(configurations.find((c) => c.id === selectedConfiguration.id) as Configuration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurations]);

  // IF NO CLIENT REDIRECT TO PREVIOUS PAGE (ClientSelectView)
  if (!client) return <Redirect to="/clients" />;

  const onSelectorChanged = (_: React.MouseEvent<HTMLButtonElement, MouseEvent>, v?: number | string | null) => {
    if (v) {
      setTabSelected(v as Selections);
    }
    resetAction();
  };

  const onEnvironmentChange = (clientIntegration: ClientIntegration) => {
    setEnvironment(clientIntegration);
    setGenre('');
    setEvent('');
    setVenue('');
  };

  const showLoading = loading || configurations.length === 0;

  return (
    <>
      {/* HEADER ROW */}
      <Grid className={classes.mediaCenterNotAvailable}>
        <MuiTypography className={classes.mediaNotAvailableMessage}>
          This functionality is not available with the current screen size or orientation.
        </MuiTypography>
      </Grid>
      <Grid className={classes.mediaCenterContainer}>
        <Flex direction="column" padding="7 7 0" justify="between">
          <Box flexOverride="none">
            <MuiTypography className={classes.mediaCenterHeaderText}>Media Center</MuiTypography>
            {showLoading ? (
              <Flex noWrap gutter={2} padding="6 0" data-testid="media-loading">
                <Skeleton variant="text" height={96} width={202} />
                <Skeleton variant="text" height={96} width={202} />
                <Skeleton variant="text" height={96} width={202} />
              </Flex>
            ) : (
              <Flex align="center" margin="6 0" direction="row">
                <MuiBox className={classes.mediaCenterSelectorPills}>
                  <Selector onChange={onSelectorChanged} value={tabSelected} size="small">
                    <Selector.Item value={Selections.LOWER_DECK} label="Main Screen" />
                    {upperMonitor && <Selector.Item value={Selections.UPPER_DECK} label="Upper Screen" />}
                    <Selector.Item value={Selections.OTHER_IMAGES} label="Other Images" />
                    {genreTab && <Selector.Item value={Selections.GENRE_IMAGES} label="Genre Images" />}
                    <Selector.Item value={Selections.EVENT_IMAGES} label="Event Images" />
                    <Selector.Item value={Selections.VENUE_IMAGES} label="Venue Images" />
                  </Selector>
                </MuiBox>

                {selectedConfiguration && showConfigSelector && (
                  <Flex fullWidth margin="6 0 0" align="center">
                    <Typography size={24} weight={700}>
                      Configuration
                    </Typography>
                    <Box width={20} />
                    <SelectDropdown
                      items={getSortedList(configurations, 'name')}
                      itemToString={(item: Configuration) => item.name}
                      selectedItem={selectedConfiguration}
                      onChange={setConfiguration}
                      data-testid="media-selector"
                    />
                  </Flex>
                )}
                {showEnvironmentSelector && (
                  <Flex fullWidth margin="6 0 0" align="center">
                    <Typography size={24} weight={700} style={{ marginRight: '20px' }}>
                      <Box width={180}>Environment</Box>
                    </Typography>
                    <SelectDropdown
                      items={getSortedList(clientIntegrations, 'name')}
                      itemToString={(item: ClientIntegration) => item && item.name}
                      selectedItem={selectedCI as ClientIntegration}
                      onChange={(item: ClientIntegration) => onEnvironmentChange(item)}
                      placeholder="Select an environment"
                      data-testid="environment-dropdown"
                    />
                  </Flex>
                )}
              </Flex>
            )}
          </Box>
        </Flex>

        {/* MEDIADETAILS COMPONENTS */}
        {selectedConfiguration && (
          <Flex padding="0 7 7" noWrap height="581px">
            {tabSelected === Selections.UPPER_DECK && <UpperDeckMediaDetails configuration={selectedConfiguration} />}
            {tabSelected === Selections.LOWER_DECK && <AttractLoopMediaDetails configuration={selectedConfiguration} />}
            {tabSelected === Selections.OTHER_IMAGES && <OtherImages configuration={selectedConfiguration} />}
            {tabSelected === Selections.EVENT_IMAGES && <EventImages environment={selectedCI} />}
            {tabSelected === Selections.VENUE_IMAGES && <VenueImages environment={selectedCI} />}
            {tabSelected === Selections.GENRE_IMAGES && <GenreImages environment={selectedCI} />}
          </Flex>
        )}
      </Grid>
    </>
  );
};

export default MediaCenterView;
